import React, { Reducer, useCallback, useReducer, useState } from 'react';
import { nanoid } from 'nanoid';

import { StreamMessage, useChannelStateContext } from '../../../context/ChannelStateContext';

import { useAttachments } from './useAttachments';
import { useMessageInputText } from './useMessageInputText';
import { useSubmitHandler } from './useSubmitHandler';
import { usePasteHandler } from './usePasteHandler';

import type { FileLike } from '../../ReactFileUtilities';
import type { Attachment, Message, OGAttachment, UserResponse } from 'belo-js-sdk';

import type { MessageInputProps } from '../MessageInput';

import type {
  CustomTrigger,
  DefaultBeloChatGenerics,
  SendMessageOptions,
} from '../../../types/types';
import { EnrichURLsController, useLinkPreviews } from './useLinkPreviews';
import type { FileUpload, ImageUpload, LinkPreviewMap } from '../types';
import { LinkPreviewState, SetLinkPreviewMode } from '../types';

export type MessageInputState<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = {
  attachments: Attachment<BeloChatGenerics>[];
  fileOrder: string[];
  fileUploads: Record<string, FileUpload>;
  imageOrder: string[];
  imageUploads: Record<string, ImageUpload>;
  linkPreviews: LinkPreviewMap;
  mentioned_users: UserResponse<BeloChatGenerics>[];
  setText: (text: string) => void;
  text: string;
};

type SetTextAction = {
  getNewText: (currentStateText: string) => string;
  type: 'setText';
};

type ClearAction = {
  type: 'clear';
};

type SetImageUploadAction = {
  id: string;
  type: 'setImageUpload';
  file?: File | FileLike;
  previewUri?: string;
  state?: string;
  url?: string;
};

type SetFileUploadAction = {
  id: string;
  type: 'setFileUpload';
  file?: File;
  state?: string;
  thumb_url?: string;
  url?: string;
};

type SetLinkPreviewsAction = {
  linkPreviews: LinkPreviewMap;
  mode: SetLinkPreviewMode;
  type: 'setLinkPreviews';
};

type RemoveImageUploadAction = {
  id: string;
  type: 'removeImageUpload';
};

type RemoveFileUploadAction = {
  id: string;
  type: 'removeFileUpload';
};

type AddMentionedUserAction<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = {
  type: 'addMentionedUser';
  user: UserResponse<BeloChatGenerics>;
};

export type MessageInputReducerAction<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> =
  | SetTextAction
  | ClearAction
  | SetImageUploadAction
  | SetFileUploadAction
  | SetLinkPreviewsAction
  | RemoveImageUploadAction
  | RemoveFileUploadAction
  | AddMentionedUserAction<BeloChatGenerics>;

export type MessageInputHookProps<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = EnrichURLsController & {
  handleChange: React.ChangeEventHandler<HTMLTextAreaElement>;
  handleSubmit: (
    event: React.BaseSyntheticEvent,
    customMessageData?: Partial<Message<BeloChatGenerics>>,
    options?: SendMessageOptions,
  ) => void;
  insertText: (textToInsert: string) => void;
  isUploadEnabled: boolean;
  maxFilesLeft: number;
  numberOfUploads: number;
  onPaste: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onSelectUser: (item: UserResponse<BeloChatGenerics>) => void;
  removeFile: (id: string) => void;
  removeImage: (id: string) => void;
  textareaRef: React.MutableRefObject<HTMLTextAreaElement | null | undefined>;
  uploadFile: (id: string) => void;
  uploadImage: (id: string) => void;
  uploadNewFiles: (files: FileList | File[]) => void;
};

const makeEmptyMessageInputState = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(): MessageInputState<BeloChatGenerics> => ({
  attachments: [],
  fileOrder: [],
  fileUploads: {},
  imageOrder: [],
  imageUploads: {},
  linkPreviews: new Map(),
  mentioned_users: [],
  setText: () => null,
  text: '',
});

/**
 * Initializes the state. Empty if the message prop is falsy.
 */
const initState = <BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics>(
  message?: Pick<StreamMessage<BeloChatGenerics>, 'attachments' | 'mentioned_users' | 'text'>,
): MessageInputState<BeloChatGenerics> => {
  if (!message) {
    return makeEmptyMessageInputState();
  }

  // if message prop is defined, get image uploads, file uploads, text, etc.
  const imageUploads =
    message.attachments
      ?.filter(({ type }) => type === 'image')
      .reduce<Record<string, ImageUpload>>(
        (
          acc,
          { author_name, fallback = '', image_url, og_scrape_url, text, title, title_link },
        ) => {
          const id = nanoid();
          acc[id] = {
            author_name,
            file: {
              name: fallback,
            },
            id,
            og_scrape_url,
            state: 'finished',
            text,
            title,
            title_link,
            url: image_url,
          };
          return acc;
        },
        {},
      ) ?? {};

  const fileUploads =
    message.attachments
      ?.filter(({ type }) => type === 'file')
      .reduce<Record<string, FileUpload>>(
        (acc, { asset_url, file_size, mime_type, thumb_url, title = '' }) => {
          const id = nanoid();
          acc[id] = {
            file: {
              name: title,
              size: file_size,
              type: mime_type,
            },
            id,
            state: 'finished',
            thumb_url,
            url: asset_url,
          };
          return acc;
        },
        {},
      ) ?? {};

  const linkPreviews =
    message.attachments?.reduce<LinkPreviewMap>((acc, attachment) => {
      if (!attachment.og_scrape_url) return acc;
      acc.set(attachment.og_scrape_url, {
        ...(attachment as OGAttachment),
        state: LinkPreviewState.LOADED,
      });
      return acc;
    }, new Map()) ?? new Map();

  const imageOrder = Object.keys(imageUploads);
  const fileOrder = Object.keys(fileUploads);

  const attachments =
    message.attachments?.filter(({ type }) => type !== 'file' && type !== 'image') || [];

  const mentioned_users: StreamMessage['mentioned_users'] = message.mentioned_users || [];

  return {
    attachments,
    fileOrder,
    fileUploads,
    imageOrder,
    imageUploads,
    linkPreviews,
    mentioned_users,
    setText: () => null,
    text: message.text || '',
  };
};

/**
 * MessageInput state reducer
 */
const messageInputReducer = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(
  state: MessageInputState<BeloChatGenerics>,
  action: MessageInputReducerAction<BeloChatGenerics>,
) => {
  switch (action.type) {
    case 'setText':
      return { ...state, text: action.getNewText(state.text) };

    case 'clear':
      return makeEmptyMessageInputState();

    case 'setImageUpload': {
      const imageAlreadyExists = state.imageUploads[action.id];
      if (!imageAlreadyExists && !action.file) return state;
      const imageOrder = imageAlreadyExists ? state.imageOrder : state.imageOrder.concat(action.id);
      const newUploadFields = { ...action } as Partial<SetImageUploadAction>;
      delete newUploadFields.type;
      return {
        ...state,
        imageOrder,
        imageUploads: {
          ...state.imageUploads,
          [action.id]: { ...state.imageUploads[action.id], ...newUploadFields },
        },
      };
    }

    case 'setFileUpload': {
      const fileAlreadyExists = state.fileUploads[action.id];
      if (!fileAlreadyExists && !action.file) return state;
      const fileOrder = fileAlreadyExists ? state.fileOrder : state.fileOrder.concat(action.id);
      const newUploadFields = { ...action } as Partial<SetFileUploadAction>;
      delete newUploadFields.type;
      return {
        ...state,
        fileOrder,
        fileUploads: {
          ...state.fileUploads,
          [action.id]: { ...state.fileUploads[action.id], ...newUploadFields },
        },
      };
    }

    case 'setLinkPreviews': {
      const linkPreviews = new Map(state.linkPreviews);

      if (action.mode === SetLinkPreviewMode.REMOVE) {
        Array.from(action.linkPreviews.keys()).forEach((key) => {
          linkPreviews.delete(key);
        });
      } else {
        Array.from(action.linkPreviews.values()).reduce<LinkPreviewMap>((acc, linkPreview) => {
          const existingPreview = acc.get(linkPreview.og_scrape_url);
          const alreadyEnqueued =
            linkPreview.state === LinkPreviewState.QUEUED &&
            existingPreview?.state !== LinkPreviewState.FAILED;

          if (existingPreview && alreadyEnqueued) return acc;
          acc.set(linkPreview.og_scrape_url, linkPreview);
          return acc;
        }, linkPreviews);

        if (action.mode === SetLinkPreviewMode.SET) {
          Array.from(state.linkPreviews.keys()).forEach((key) => {
            if (!action.linkPreviews.get(key)) linkPreviews.delete(key);
          });
        }
      }

      return {
        ...state,
        linkPreviews,
      };
    }

    case 'removeImageUpload': {
      if (!state.imageUploads[action.id]) return state; // cannot remove anything
      const newImageUploads = { ...state.imageUploads };
      delete newImageUploads[action.id];
      return {
        ...state,
        imageOrder: state.imageOrder.filter((_id) => _id !== action.id),
        imageUploads: newImageUploads,
      };
    }

    case 'removeFileUpload': {
      if (!state.fileUploads[action.id]) return state; // cannot remove anything
      const newFileUploads = { ...state.fileUploads };
      delete newFileUploads[action.id];
      return {
        ...state,
        fileOrder: state.fileOrder.filter((_id) => _id !== action.id),
        fileUploads: newFileUploads,
      };
    }

    case 'addMentionedUser':
      return {
        ...state,
        mentioned_users: state.mentioned_users.concat(action.user),
      };

    default:
      return state;
  }
};

export type CommandsListState = {
  closeCommandsList: () => void;
  openCommandsList: () => void;
  showCommandsList: boolean;
};

export type MentionsListState = {
  closeMentionsList: () => void;
  openMentionsList: () => void;
  showMentionsList: boolean;
};

/**
 * hook for MessageInput state
 */
export const useMessageInputState = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics,
  V extends CustomTrigger = CustomTrigger
>(
  props: MessageInputProps<BeloChatGenerics, V>,
): MessageInputState<BeloChatGenerics> &
  MessageInputHookProps<BeloChatGenerics> &
  CommandsListState &
  MentionsListState => {
  const { additionalTextareaProps, getDefaultValue, message, urlEnrichmentConfig } = props;

  const {
    channelCapabilities = {},
    channelConfig,
    enrichURLForPreview: enrichURLForPreviewChannelContext,
  } = useChannelStateContext<BeloChatGenerics>('useMessageInputState');

  const defaultValue = getDefaultValue?.() || additionalTextareaProps?.defaultValue;
  const initialStateValue =
    message ||
    ((Array.isArray(defaultValue)
      ? { text: defaultValue.join('') }
      : { text: defaultValue?.toString() }) as Partial<StreamMessage<BeloChatGenerics>>);

  const [state, dispatch] = useReducer(
    messageInputReducer as Reducer<
      MessageInputState<BeloChatGenerics>,
      MessageInputReducerAction<BeloChatGenerics>
    >,
    initialStateValue,
    initState,
  );

  const enrichURLsController = useLinkPreviews({
    dispatch,
    linkPreviews: state.linkPreviews,
    ...urlEnrichmentConfig,
    enrichURLForPreview:
      urlEnrichmentConfig?.enrichURLForPreview ?? enrichURLForPreviewChannelContext,
  });

  const { handleChange, insertText, textareaRef } = useMessageInputText<BeloChatGenerics, V>(
    props,
    state,
    dispatch,
    enrichURLsController.findAndEnqueueURLsToEnrich,
  );

  const [showCommandsList, setShowCommandsList] = useState(false);
  const [showMentionsList, setShowMentionsList] = useState(false);

  const openCommandsList = () => {
    dispatch({
      getNewText: () => '/',
      type: 'setText',
    });
    setShowCommandsList(true);
  };

  const closeCommandsList = () => setShowCommandsList(false);

  const openMentionsList = () => {
    dispatch({
      getNewText: (currentText) => currentText + '@',
      type: 'setText',
    });
    setShowMentionsList(true);
  };

  const closeMentionsList = () => setShowMentionsList(false);

  const {
    maxFilesLeft,
    numberOfUploads,
    removeFile,
    removeImage,
    uploadFile,
    uploadImage,
    uploadNewFiles,
  } = useAttachments<BeloChatGenerics, V>(props, state, dispatch, textareaRef);

  const { handleSubmit } = useSubmitHandler<BeloChatGenerics, V>(
    props,
    state,
    dispatch,
    numberOfUploads,
    enrichURLsController,
  );
  const isUploadEnabled =
    channelConfig?.uploads !== false && channelCapabilities['upload-file'] !== false;

  const { onPaste } = usePasteHandler(
    uploadNewFiles,
    insertText,
    isUploadEnabled,
    enrichURLsController.findAndEnqueueURLsToEnrich,
  );

  const onSelectUser = useCallback((item: UserResponse<BeloChatGenerics>) => {
    dispatch({ type: 'addMentionedUser', user: item });
  }, []);

  const setText = useCallback((text: string) => {
    dispatch({ getNewText: () => text, type: 'setText' });
  }, []);

  return {
    ...state,
    ...enrichURLsController,
    closeCommandsList,
    closeMentionsList,
    handleChange,
    handleSubmit,
    insertText,
    isUploadEnabled,
    maxFilesLeft,
    numberOfUploads,
    onPaste,
    onSelectUser,
    openCommandsList,
    openMentionsList,
    removeFile,
    removeImage,
    setText,
    showCommandsList,
    showMentionsList,
    textareaRef,
    uploadFile,
    uploadImage,
    uploadNewFiles,
  };
};
